/** The WebRTC side of a live conversation.
 *
 * The browser holds the session against the vendor itself — that is what
 * makes speech-to-speech fast enough to be a conversation rather than a
 * walkie-talkie. Our server is involved exactly twice: once to reserve the
 * window and mint a short-lived credential, once to settle up afterwards.
 *
 * Two things this hook owns, and both are about money rather than audio:
 *
 *   * the CLOCK. The reserved window is finite and the learner is paying
 *     by the minute, so the time left is shown and the session closes
 *     itself at zero rather than relying on the credential expiring;
 *   * ENDING. A conversation that is never ended is charged for the whole
 *     window. The server settles it eventually either way, so this is not
 *     a correctness problem — it is the difference between a learner
 *     getting their money back in a second and not getting it back at all.
 *     Hence an unmount closes the session too, not just the stop button.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../../api/liveVoice";
import type { LiveVoiceEnded, LiveVoiceSession } from "../../api/liveVoice";

/** Where the browser exchanges SDP with the vendor, holding only the
 *  ephemeral credential. The model is already fixed in the minted session,
 *  which is why there is no query string here. */
const CALLS_URL = "https://api.openai.com/v1/realtime/calls";

export type LiveState = "idle" | "connecting" | "live" | "ended";

export function useLiveVoice() {
  const [state, setState] = useState<LiveState>("idle");
  const [session, setSession] = useState<LiveVoiceSession | null>(null);
  const [result, setResult] = useState<LiveVoiceEnded | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Read by the unmount cleanup, which must not depend on React state it
  // may never see again.
  const openIdRef = useRef<string | null>(null);

  /** Tear the connection down. Safe to call twice — a stop button and an
   *  unmount both firing is the normal case, not an edge one. */
  const teardown = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
  }, []);

  const stop = useCallback(async () => {
    const id = openIdRef.current;
    teardown();
    openIdRef.current = null;
    if (!id) return;
    setState("ended");
    try {
      setResult(await api.endLiveVoice(id));
    } catch {
      // The server settles an abandoned window on its own, so a failed
      // end costs the learner the unused time rather than breaking
      // anything. Not worth an error on screen at the moment they are
      // leaving.
    }
  }, [teardown]);

  const start = useCallback(
    async (opts: { model?: string | null; language?: string; level?: string | null }) => {
      setError(null);
      setResult(null);
      setState("connecting");
      let opened: LiveVoiceSession | null = null;
      try {
        // Reserve FIRST. If this refuses (no balance) nothing else has
        // happened yet — no microphone prompt, no connection.
        opened = await api.startLiveVoice(opts);
        setSession(opened);
        openIdRef.current = opened.id;
        setSecondsLeft(opened.max_seconds);

        const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        micRef.current = mic;

        const pc = new RTCPeerConnection();
        pcRef.current = pc;
        pc.ontrack = (e) => {
          if (audioRef.current) audioRef.current.srcObject = e.streams[0];
        };
        mic.getTracks().forEach((t) => pc.addTrack(t, mic));
        // The vendor expects an events channel even when we send nothing
        // down it; without one the session never reaches a usable state.
        pc.createDataChannel("oai-events");

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        const answer = await fetch(CALLS_URL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${opened.client_secret}`,
            "Content-Type": "application/sdp",
          },
          body: offer.sdp,
        });
        if (!answer.ok) throw new Error(`realtime handshake failed (${answer.status})`);
        await pc.setRemoteDescription({
          type: "answer",
          sdp: await answer.text(),
        } as RTCSessionDescriptionInit);

        setState("live");
      } catch (e) {
        // Anything from here on leaves a RESERVED window the learner is
        // not using, so give it straight back rather than making them
        // wait out a conversation that never happened.
        teardown();
        setError(e instanceof Error ? e.message : "could not start");
        if (opened) {
          openIdRef.current = null;
          try {
            setResult(await api.endLiveVoice(opened.id));
          } catch {
            /* settled by the server's sweep instead */
          }
        }
        setState("idle");
      }
    },
    [teardown],
  );

  // The clock. Counts the reserved window down and closes the session at
  // zero, so the learner sees it end rather than hearing it stop.
  useEffect(() => {
    if (state !== "live") return;
    const tick = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          void stop();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [state, stop]);

  // Leaving the page ends the conversation. Without this a learner who
  // navigates away pays for the rest of their window.
  useEffect(() => {
    return () => {
      if (openIdRef.current) void stop();
      else teardown();
    };
  }, [stop, teardown]);

  return { state, session, result, secondsLeft, error, start, stop, audioRef };
}
